import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 } from 'uuid';
import { CustomFieldContext } from '../../../../../../context/CustomFieldContext';
import { OrderContext } from '../../../../../../context/OrderContext';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import { CreateOrderResponse, CreateOrderVariables, OrderMutations } from '../../../../../../graphql/order.graphql';
import useMutation from '../../../../../../hooks/useMutation';
import { useToast } from '../../../../../../providers/ToastProvider';
import { CreateOrderInput } from '../../../../../../types/order/order.input';
import { Product } from '../../../../../../types/product';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { translate } from '../../../../../../types/translation/Translator';
import { UnitOfMeasure } from '../../../../../../types/uom';
import { toMap } from '../../../../../../util/map.util';
import { Routing } from '../../../../../../util/routes.util';
import { CancelButton, CreateButton } from '../../../../../components/Button';
import { Divider } from '../../../../../components/Divider';
import ActionModal, { ActionModalBody, ActionModalFooter } from '../../../../../components/Modal/templates/ActionModal';
import { TextInput } from '../../../../../components/TextInput';
import { ToastType } from '../../../../../components/Toast';
import CreateOrderInfoPane from '../../../../Operation/Order/Modals/Panes/CreateOrderInfoPane';
import { FlowType } from '../../../../../../types/order/orderFlow';

interface CreateOrderFromStockModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  product: Product;
}

export default function CreateOrderFromStockModal({ open, setOpen, product }: CreateOrderFromStockModalProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { productMasterData } = useContext(ProductMasterDataContext);
  const { customFields } = useContext(CustomFieldContext);
  const { orders, setOrders } = useContext(OrderContext);

  const pmd = productMasterData.get(product.productMasterDataId);

  if (!pmd) return null;

  const [input, setInput] = useState(
    new CreateOrderInput({
      companyId: product.companyId,
      type: FlowType.move,
      fromStockLocationId: product.stockLocationId,
    }),
  );

  const [quantity, setQuantity] = useState(UnitOfMeasure.toQuantity(pmd.unitOfMeasure, product.unitQuantity));

  const [createOrder, { loading }] = useMutation<CreateOrderResponse, CreateOrderVariables>(OrderMutations.create, {
    onCompleted: response => {
      const order = response.createOrder[0];
      setOrders(new Map(orders.set(order.id, order)));
      setOpen(false);
      showToast({
        type: ToastType.success,
        title: translate({ key: TranslationKey.orderCreatedToastTitle }),
        content: translate({ key: TranslationKey.orderCreatedToastContentClickToStart }),
        onClick: () => navigate(Routing.operations.orders.update(order.id).detail()),
      });
    },
  });

  return (
    <ActionModal open={open} setOpen={setOpen} title={translate({ key: TranslationKey.stockTransfer })}>
      <ActionModalBody>
        <div className='grid grid-cols-2 gap-2'>
          <TextInput label={translate({ key: TranslationKey.product })} value={pmd.productName} disabled={true} />
          <TextInput
            label={translate({ key: TranslationKey.quantity })}
            type={'number'}
            value={quantity}
            onChange={value => setQuantity(value)}
            disabled={pmd.kitManaged || pmd.serialManaged}
          />
        </div>
        <Divider className='my-6' />
        <CreateOrderInfoPane order={input} setOrder={setInput} next={() => {}} />
      </ActionModalBody>
      <ActionModalFooter>
        <div className='flex justify-between'>
          <CancelButton onClick={() => setOpen(false)} disabled={loading} />
          <CreateButton
            loading={loading}
            onClick={() => {
              const identifierCfs = toMap(Product.fromIdentifier(customFields, product.identifier), 'id');
              input.products = new Map().set(v4(), {
                pmdId: product.productMasterDataId,
                fromStockLocationId: product.stockLocationId,
                fromBinId: product.binId,
                fromContainerId: product.containerId,
                serialNbr: product.serial,
                lpn: product.lpn,
                toCustomFields: identifierCfs,
                fromCustomFields: identifierCfs,
                quantity: parseFloat(quantity),
                kit: product.kitId
                  ? {
                      fromKitId: product.kitId,
                      toKitId: product.kitId,
                    }
                  : undefined,
              });

              createOrder({ variables: { orders: [input.forCreate()] } });
            }}
            // disabled={!input.validate(customFields)}
          />
        </div>
      </ActionModalFooter>
    </ActionModal>
  );
}
